import React, { useRef, useEffect } from "react"
import PropTypes from "prop-types"
import { Swiper, SwiperSlide } from "swiper/react"
import { FreeMode, Navigation, Pagination, Scrollbar } from "swiper/modules"
import { LazyLoadImage } from "react-lazy-load-image-component"
import ImagePlaceholder from "components/common/ImagePlaceholder"
import Icon from "components/common/Icon"
import styles from "./MediaCarousel.module.css"

const isVideo = (item) => item?.type === "video" || item?.type === "trimmed_video"

const getThumbSrc = (item, preview) => {
  if (isVideo(item)) return item.thumbnail_url || item.preview_url || item.placeholder_url
  return preview && item.preview_url ? item.preview_url : item.url
}

const getSlideClassName = (slidesPerView, ratio) => {
  const widthClass = slidesPerView === 1 ? "w-100" : ""
  const heightClass = ratio ? `h-auto ratio ratio-${ratio}` : "h-100"
  return [widthClass, heightClass].filter(Boolean).join(" ")
}

const MediaCarousel = ({
  photos = [],
  cropped_photos = [],
  preview = false,
  ratio = "4x3",
  slidesPerView = 1,
  className,
  swiperClassName,
  onMediaClick,
  ...rest
}) => {
  const allPhotos = photos.length ? photos : cropped_photos.map((photo) => ({ ...photo, type: "cropped_photos" }))
  const sortedPhotos = [...allPhotos].sort((a, b) => (a.position ?? Infinity) - (b.position ?? Infinity))

  const slideLeftRef = useRef(null)
  const slideRightRef = useRef(null)
  const swiperRef = useRef(null)

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.params.navigation.prevEl = slideLeftRef.current
      swiperRef.current.params.navigation.nextEl = slideRightRef.current
      swiperRef.current.navigation.init()
      swiperRef.current.navigation.update()
    }
  }, [swiperRef.current, slideLeftRef.current, slideRightRef.current])

  const outerClass = ["mw-100 w-100", className].filter(Boolean).join(" ")
  const swiperClass = ["hover-target bg-gray-lightest h-100", swiperClassName].filter(Boolean).join(" ")
  const slideClass = getSlideClassName(slidesPerView, ratio)

  return (
    <div className={outerClass} style={{ height: "100%" }} {...rest}>
      <Swiper
        onSwiper={(swiper) => (swiperRef.current = swiper)}
        slidesPerView={slidesPerView}
        spaceBetween={slidesPerView > 1 ? 8 : 0}
        modules={[FreeMode, Scrollbar, Navigation, Pagination]}
        pagination={{ horizontalClass: "bottom-0" }}
        className={swiperClass}
      >
        {sortedPhotos.length ? (
          sortedPhotos.map((item) => {
            const thumbSrc = getThumbSrc(item, preview)
            const videoItem = isVideo(item)
            return (
              <SwiperSlide
                key={item.id}
                className={slideClass}
                onClick={videoItem && onMediaClick ? () => onMediaClick(item) : undefined}
                style={videoItem && onMediaClick ? { cursor: "pointer" } : undefined}
              >
                <div className={styles.slideWrapper}>
                  <img src={thumbSrc} alt="" className={styles.mirrorBackground} />
                  <LazyLoadImage
                    src={thumbSrc}
                    alt=""
                    wrapperProps={{ className: styles.imageWrapper }}
                    className={styles.mainImage}
                    effect="blur"
                    placeholderSrc={item.placeholder_url || null}
                  />
                  {videoItem && (
                    <div className={styles.playOverlay}>
                      <div className={styles.playButton}>
                        <Icon iconName="Play" width={24} height={24} className="text-white" />
                      </div>
                    </div>
                  )}
                </div>
              </SwiperSlide>
            )
          })
        ) : (
          <SwiperSlide className={slideClass}>
            <ImagePlaceholder label={false} />
          </SwiperSlide>
        )}
        {sortedPhotos.length > 0 && (
          <>
            <div
              className="swiper-button-prev top-0 start-0 m-0 p-2 h-100 w-auto bg-white bg-opacity-0 bg-opacity-25-hover transition hover-target__show-on-hover"
              ref={slideLeftRef}
            >
              <div className="bg-dark bg-opacity-50 rounded-circle position-absolute" style={{ width: 25, height: 25 }} />
              <Icon iconName="Dropdown" width={27} height={27} className="text-white rotate-90 w-auto h-auto" />
            </div>
            <div
              className="swiper-button-next top-0 end-0 m-0 p-2 h-100 w-auto bg-white bg-opacity-0 bg-opacity-25-hover transition hover-target__show-on-hover"
              ref={slideRightRef}
            >
              <div className="bg-dark bg-opacity-50 rounded-circle position-absolute" style={{ width: 25, height: 25 }} />
              <Icon iconName="Dropdown" width={27} height={27} className="text-white rotate-270 w-auto h-auto" />
            </div>
          </>
        )}
      </Swiper>
    </div>
  )
}

MediaCarousel.propTypes = {
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      url: PropTypes.string,
      preview_url: PropTypes.string,
      placeholder_url: PropTypes.string,
      thumbnail_url: PropTypes.string,
      type: PropTypes.string,
      position: PropTypes.number
    })
  ),
  cropped_photos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      url: PropTypes.string.isRequired,
      preview_url: PropTypes.string,
      placeholder_url: PropTypes.string
    })
  ),
  preview: PropTypes.bool,
  ratio: PropTypes.string,
  slidesPerView: PropTypes.number,
  className: PropTypes.string,
  swiperClassName: PropTypes.string,
  onMediaClick: PropTypes.func
}

export default MediaCarousel
